import { iterableEnumerate } from "collection-utils";
import type {
    ScreenDescription,
    ComponentDescription,
    ContainerComponentDescription} from "./description";
import {
    isClassOrArrayScreenDescription,
    getScreenComponents,
} from "./description";
import { assertNever } from "../Support";
import { handlerForComponentKind } from "./handlers";
import type { ComponentIndexes } from "../component-indexes";

export function getSubComponents(desc: ComponentDescription): readonly ComponentDescription[] | undefined {
    const handler = handlerForComponentKind(desc.kind);
    if (handler?.supportsSubComponents !== true) return undefined;

    const containerDesc = desc as ContainerComponentDescription;
    return containerDesc.components ?? [];
}

// Returns [component, top-level].  Those two are the same if `indexes` has
// length 1.  If not, then `top-level` is the parent of `component`.
export function getComponentAtIndexes(
    screen: ScreenDescription,
    indexes: ComponentIndexes
): [ComponentDescription, ComponentDescription] | [undefined, ComponentDescription | undefined] {
    if (!isClassOrArrayScreenDescription(screen)) return [undefined, undefined];
    const components = getScreenComponents(screen);
    const parent = components[indexes[0]];
    if (indexes.length === 1) {
        return [parent, parent];
    } else if (indexes.length === 2) {
        if (parent === undefined) return [undefined, undefined];
        const subComponents = getSubComponents(parent);
        return [subComponents?.[indexes[1]], parent];
    } else {
        return assertNever(indexes);
    }
}

export function nextComponentIndex(screen: ScreenDescription, indexes: ComponentIndexes): ComponentIndexes | undefined {
    if (!isClassOrArrayScreenDescription(screen)) return undefined;
    const components = getScreenComponents(screen);

    if (indexes.length === 1) {
        if (indexes[0] + 1 >= components.length) return undefined;
        return [indexes[0] + 1];
    } else if (indexes.length === 2) {
        const [, parent] = getComponentAtIndexes(screen, indexes);
        if (parent === undefined) return undefined;
        const subComponents = getSubComponents(parent);
        if (subComponents === undefined || indexes[1] + 1 >= subComponents.length) {
            if (indexes[0] + 1 >= components.length) return undefined;
            return [indexes[0] + 1];
        } else {
            return [indexes[0], indexes[1] + 1];
        }
    } else {
        return assertNever(indexes);
    }
}

export function prevComponentIndex(indexes: ComponentIndexes): ComponentIndexes {
    if (indexes.length === 1) {
        return [Math.max(0, indexes[0] - 1)];
    } else if (indexes.length === 2) {
        return [indexes[0], Math.max(0, indexes[1] - 1)];
    } else {
        return assertNever(indexes);
    }
}

export function findComponentIndex(
    screen: ScreenDescription,
    component: ComponentDescription
): ComponentIndexes | undefined {
    if (!isClassOrArrayScreenDescription(screen)) return undefined;
    const components = getScreenComponents(screen);

    for (const [index, parent] of iterableEnumerate(components)) {
        if (parent === component) {
            return [index];
        }
        const subComponents = getSubComponents(parent);
        if (subComponents === undefined) continue;
        for (const [subIndex, sub] of iterableEnumerate(subComponents)) {
            if (sub === component) {
                return [index, subIndex];
            }
        }
    }
    return undefined;
}

export function findComponentWithID(
    screen: ScreenDescription,
    componentID: string
): [ComponentDescription, ComponentIndexes] | undefined {
    if (!isClassOrArrayScreenDescription(screen)) return undefined;
    const components = getScreenComponents(screen);

    for (const [index, parent] of iterableEnumerate(components)) {
        if (parent.componentID === componentID) {
            return [parent, [index]];
        }
        const subComponents = getSubComponents(parent);
        if (subComponents === undefined) continue;
        for (const [subIndex, sub] of iterableEnumerate(subComponents)) {
            if (sub.componentID === componentID) {
                return [sub, [index, subIndex]];
            }
        }
    }
    return undefined;
}
